import { useState, type FormEvent } from "react";
import { useApi, type CreatedToken } from "../lib/api.ts";
import { CopyField } from "./CopyField.tsx";

interface Props {
  onClose: () => void;
  /** Called after a token is successfully created so the list can refresh. */
  onCreated: () => void;
}

export function TokenCreateModal({ onClose, onCreated }: Props) {
  const api = useApi();
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [created, setCreated] = useState<CreatedToken>();

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || submitting) return;
    setSubmitting(true);
    setError(undefined);
    try {
      const token = await api.createToken(name.trim());
      setCreated(token);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label="Create API token"
        onClick={(e) => e.stopPropagation()}
      >
        {!created ? (
          <form onSubmit={submit}>
            <h2 className="modal-title">Create API token</h2>
            <p className="modal-sub">
              Give the token a name so you can recognize it later (e.g. the
              machine or CI job that uses it).
            </p>
            <label className="field">
              <span className="field-label">Name</span>
              <input
                className="input"
                autoFocus
                value={name}
                placeholder="laptop"
                maxLength={64}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            {error && <p className="form-error">{error}</p>}
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn--primary"
                disabled={!name.trim() || submitting}
              >
                {submitting ? "Creating…" : "Create token"}
              </button>
            </div>
          </form>
        ) : (
          <div>
            <h2 className="modal-title">Token created</h2>
            <div className="warning-banner">
              <strong>Copy this now.</strong> This is the only time the secret
              will be shown — you won&apos;t be able to see it again.
            </div>
            <CopyField value={created.token} label={created.name} multiline />
            <p className="modal-sub">
              Use it with the CLI:
            </p>
            <CopyField
              value={`stift login https://sync.stift.sh --token ${created.token}`}
              prompt="$"
              multiline
            />
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn--primary"
                onClick={onClose}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
